import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import AnimatedNumber from "@/components/AnimatedNumber";
import { CATEGORY_ICONS, CATEGORY_COLORS, money, fmtDate } from "@/lib/utils2";
import { PushBanner } from "@/components/PushControls";

const EMERGENCY = [
  { label: "Gardien", num: "+212600000005", icon: "Shield" },
  { label: "Président", num: "+212600000001", icon: "User" },
  { label: "Pompiers", num: "15", icon: "Flame" },
  { label: "Police", num: "19", icon: "Siren" },
];

function CatIcon({ category, className }) {
  const name = CATEGORY_ICONS[category] || "Package";
  const Ico = Icons[name] || Icons.Package;
  return <Ico className={className} />;
}

export default function Home() {
  const { t, lang } = useApp();
  const { user, isRole } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [feed, setFeed] = useState([]);
  const [anns, setAnns] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setDash(r.data));
    api.get("/feed").then((r) => setFeed(r.data.slice(0, 5)));
    api.get("/announcements").then((r) => setAnns(r.data.filter((a) => a.pinned).slice(0, 2)));
  }, []);

  if (!dash) return <div className="py-20 text-center text-muted-foreground">{t("loading")}</div>;

  const canAddExpense = isRole("president", "tresorier");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="home-page">
      <PushBanner t={t} />
      <div>
        <p className="text-muted-foreground">{t("hello")},</p>
        <h1 className="text-2xl font-bold">{user?.name}</h1>
      </div>

      {/* Hero treasury (banque + caisse liquide) */}
      <div className="relative overflow-hidden rounded-3xl bg-brand text-white p-7 shadow-[0_20px_40px_rgb(10,77,60,0.25)]" data-testid="hero-balance">
        <div className="absolute inset-0 zellige-overlay pointer-events-none" />
        <div className="relative z-10">
          <p className="text-white/70 text-sm font-semibold uppercase tracking-wider">{t("treasury_total")}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <AnimatedNumber value={dash.treasury?.total ?? dash.balance} className="text-5xl font-black tracking-tight" data-testid="hero-balance-value" />
            <span className="text-xl font-bold text-white/80">{t("currency")}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-6">
            <div className="rounded-2xl bg-white/10 p-3" data-testid="treasury-bank">
              <p className="text-white/60 text-xs font-semibold flex items-center gap-1"><Icons.Landmark className="h-3.5 w-3.5" /> {t("bank")}</p>
              <p className="text-lg font-bold">{money(dash.treasury?.bank ?? dash.bank.balance)} {t("currency")}</p>
            </div>
            <div className="rounded-2xl bg-white/10 p-3" data-testid="treasury-cash">
              <p className="text-white/60 text-xs font-semibold flex items-center gap-1"><Icons.Wallet className="h-3.5 w-3.5" /> {t("cash_box")}</p>
              <p className="text-lg font-bold">{money(dash.treasury?.cash ?? dash.cash.balance)} {t("currency")}</p>
            </div>
          </div>
          {dash.treasury?.advances_due > 0 && (
            <button onClick={() => navigate("/finances")} data-testid="advances-due-chip"
              className="mt-3 w-full rounded-2xl bg-terracotta/90 px-3 py-2 flex items-center justify-between text-sm font-semibold active:scale-[0.98] transition-transform">
              <span className="flex items-center gap-1.5"><Icons.HandCoins className="h-4 w-4" /> {t("advances_due")}</span>
              <span>{money(dash.treasury.advances_due)} {t("currency")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Reserve highlight */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f6b52] via-brand to-brand-dark text-white p-6 ring-1 ring-[#E6D5B8]/25 shadow-ios" data-testid="reserve-card">
        <div className="flex items-center gap-2 text-[#E6D5B8] text-sm font-semibold uppercase tracking-wider">
          <Icons.PiggyBank className="h-4 w-4" /> {t("reserve")}
        </div>
        <div className="flex items-baseline gap-2 mt-1">
          <AnimatedNumber value={dash.reserve} className="text-4xl font-black" />
          <span className="text-lg font-bold text-white/80">{t("currency")}</span>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        {dash.next_payment?.amount ? (
          <button onClick={() => navigate("/finances?tab=bank_account")} className="rounded-2xl bg-card border border-border p-4 text-start active:scale-95 transition-transform" data-testid="stat-next-payment">
            <Icons.Radio className="h-5 w-5 text-brand dark:text-primary mb-2" />
            <p className="text-xs text-muted-foreground font-semibold">{t("next_payment")}</p>
            <p className="font-bold text-lg">{money(dash.next_payment?.amount)} {t("currency")}</p>
            {dash.next_payment?.due_date && (
              <p className={`text-xs font-semibold mt-0.5 ${dash.next_payment.overdue ? "text-terracotta" : "text-muted-foreground"}`} data-testid="next-payment-due">
                {dash.next_payment.overdue ? t("ant_overdue") : fmtDate(dash.next_payment.due_date, lang)}
              </p>
            )}
          </button>
        ) : (
          <button onClick={() => navigate("/finances")} className="rounded-2xl bg-card border border-border p-4 text-start active:scale-95 transition-transform" data-testid="stat-available-funds">
            <Icons.Wallet className="h-5 w-5 text-brand dark:text-primary mb-2" />
            <p className="text-xs text-muted-foreground font-semibold">{t("available_funds")}</p>
            <p className="font-bold text-lg">{money(dash.treasury?.total)} {t("currency")}</p>
          </button>
        )}
        <button onClick={() => navigate("/tickets")} className="rounded-2xl bg-card border border-border p-4 text-start active:scale-95 transition-transform" data-testid="stat-tickets">
          <Icons.Wrench className="h-5 w-5 text-terracotta mb-2" />
          <p className="text-xs text-muted-foreground font-semibold">{t("open_tickets")}</p>
          <p className="font-bold text-lg">{dash.open_tickets}</p>
        </button>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="font-bold text-lg mb-3">{t("quick_actions")}</h2>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate("/action")} data-testid="quick-report"
            className="rounded-2xl bg-terracotta text-white p-4 text-start active:scale-95 transition-transform">
            <Icons.TriangleAlert className="h-6 w-6 mb-2" />
            <p className="font-bold">{t("report_problem")}</p>
          </button>
          {canAddExpense && (
            <button onClick={() => navigate("/action")} data-testid="quick-expense"
              className="rounded-2xl bg-brand text-white p-4 text-start active:scale-95 transition-transform">
              <Icons.ScanLine className="h-6 w-6 mb-2" />
              <p className="font-bold">{t("add_expense")}</p>
            </button>
          )}
        </div>
      </div>

      {/* Pinned announcements */}
      {anns.length > 0 && (
        <div>
          <h2 className="font-bold text-lg mb-3">{t("pinned_announcements")}</h2>
          <div className="space-y-2">
            {anns.map((a) => (
              <div key={a.id} className="rounded-2xl bg-accent p-4 flex gap-3" data-testid={`pinned-ann-${a.id}`}>
                <Icons.Pin className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">{a.title}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">{a.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent expenses */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-lg">{t("recent_expenses")}</h2>
          <button onClick={() => navigate("/finances")} className="text-sm font-semibold text-majorelle" data-testid="view-all-feed">
            {t("view_all")}
          </button>
        </div>
        <div className="space-y-2">
          {feed.map((item) => (
            <div key={item.id} className="rounded-2xl bg-card border border-border p-3 flex items-center gap-3" data-testid={`home-feed-${item.id}`}>
              {item.type === "revenue" ? (
                <div className="h-11 w-11 rounded-xl bg-success/15 flex items-center justify-center shrink-0">
                  <Icons.ArrowDownLeft className="h-5 w-5 text-success" />
                </div>
              ) : (
                <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: (CATEGORY_COLORS[item.category] || "#94A3B8") + "22" }}>
                  <CatIcon category={item.category} className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{item.type === "revenue" ? item.label : item.supplier}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(item.date, lang)}</p>
              </div>
              <p className={`font-bold ${item.type === "revenue" ? "text-success" : "text-foreground"}`}>
                {item.type === "revenue" ? "+" : "-"}{money(item.amount)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Emergency */}
      <div>
        <h2 className="font-bold text-lg mb-3">{t("emergency")}</h2>
        <div className="grid grid-cols-4 gap-2">
          {EMERGENCY.map((e) => {
            const Ico = Icons[e.icon] || Icons.Phone;
            return (
              <a key={e.label} href={`tel:${e.num}`} data-testid={`emergency-${e.label}`}
                className="rounded-2xl bg-card border border-border p-3 flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
                <Ico className="h-6 w-6 text-terracotta" />
                <span className="text-[11px] font-semibold text-center leading-tight">{e.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
